import { useCallback, useMemo, useState } from 'react'
import { generatePath, useHistory } from 'react-router-dom'
import { mdiArrowRight, mdiInformationOutline, mdiAlertDecagram } from '@mdi/js'
import { FormattedMessage, useIntl } from 'react-intl'
import { DateTime } from 'luxon'
import Icon from '@mdi/react'

import theme from 'theme_'
import routes from 'app/routes'
import CommonLinkButton from 'components/Button/CommonLinkButton'
import { useHomeRouteParams } from 'features/home/hooks'
import { useAppSelector } from 'app/hooks'
import IntlFormattedDate from 'app/i18n/IntlFormattedDate'
import {
  userSubscriptionPlanSelector,
  activeSubscriptionSelector,
  renewSubscriptionSelector,
  selectedHomeSelector,
} from 'features/home/selectors'
import SubscriptionIcon from 'components/Icons/SubscriptionIcon'
import { SectionLabel } from 'components/Typography'
import { SpinnerCentered } from 'components/Loader'
import { currentRootDomainIdSelector } from 'features/rootDomains/selectors'
import CancelSubscriptionModal from 'features/home/components/HomeSubscription/CancelSubscriptionModal'
import {
  getSubscriptionPlanName,
  getHomeSubscriptonInfo,
  getHomeNetworkSubscriptonInfo,
} from 'features/home/utils'
import { hasSubscriptionPermissionSelector } from 'features/auth/permissions/selectors'
import Tooltip from 'components/Tooltip'
import { CommonLink } from 'components/Button'
import { subscriptionInfoMessages } from '../../../HomeSubscription/messages'
import { isExpiringSoon } from 'features/home/components/HomeSubscription/utils'

import { StyledIcon, StyledIcoBasic } from '../../styles'
import { SubInfoBlock, SubInfoBody } from '../styles'
import {
  SubscriptionInfoExpireBlock,
  SubscriptionInfoExpiryLabel,
  SubscriptionInfoPlanBlock,
  SubscriptionInfoPlanInfo,
  SubscriptionInfoStyledManageButton,
  SubscriptionInfoBlock,
  SubscriptionInfoManagedByLabel,
  SubsInfoBottomSection,
  SubsInfoCancelSubscription,
  SubsInfoBottomSectionLeftBlock,
  SubsInfoTooltipWrapper,
} from './styles'

const SUBSCRIPTION_STATE = {
  NONE: 'NONE',
  ENROLLED: 'ENROLLED',
  WILL_NOT_RENEW: 'WILL_NOT_RENEW',
  EXPIRES: 'EXPIRES',
}

export interface SubscriptionInfoParams {
  // TODO refactor below flags
  // the component shouldn't hold any information about the places where it's being used
  isHomeDashboard: boolean
  isDetailsPage?: boolean
  hasShowMore?: boolean
}

function SubscriptionInfo(props: SubscriptionInfoParams) {
  const { homeId } = useHomeRouteParams()
  const { formatMessage } = useIntl()
  const history = useHistory()

  const [subsCancelOpen, setSubsCancelOpen] = useState(false)

  const currentRootId = useAppSelector(currentRootDomainIdSelector)
  const isFetching = useAppSelector(activeSubscriptionSelector.isFetching)
  const plan = useAppSelector(userSubscriptionPlanSelector)

  const isRenewing = useAppSelector(renewSubscriptionSelector.isFetching)
  const home = useAppSelector(selectedHomeSelector.data)
  const subscriptionInfo = getHomeSubscriptonInfo(home)
  const networkSubscriptionInfo = getHomeNetworkSubscriptonInfo(home)
  const hasSubscriptionPermission = useAppSelector(
    hasSubscriptionPermissionSelector
  )

  const subscriptionState = useMemo(() => {
    if (subscriptionInfo) {
      if (subscriptionInfo.autoRenew) return SUBSCRIPTION_STATE.ENROLLED
      return isExpiringSoon(subscriptionInfo, DateTime.now())
        ? SUBSCRIPTION_STATE.EXPIRES
        : SUBSCRIPTION_STATE.WILL_NOT_RENEW
    }
    return SUBSCRIPTION_STATE.NONE
  }, [subscriptionInfo])

  const networkSubscriptionState = useMemo(() => {
    if (networkSubscriptionInfo) {
      if (networkSubscriptionInfo.autoRenew) return SUBSCRIPTION_STATE.ENROLLED
      return isExpiringSoon(networkSubscriptionInfo, DateTime.now())
        ? SUBSCRIPTION_STATE.EXPIRES
        : SUBSCRIPTION_STATE.WILL_NOT_RENEW
    }
    return SUBSCRIPTION_STATE.NONE
  }, [networkSubscriptionInfo])

  const subscriptionEnabled = useMemo(() => {
    return !!home?.hostInfo?.hostCapabilities?.reduce((a, c) => {
      return a || (c.type === 'cloudLicenses' && c.version >= 2)
    }, false)
  }, [home])

  const subscriptionPath = generatePath(routes.homeSubscription.root(), {
    homeId,
    rootDomainId: currentRootId || '',
  })

  const openUpgradeSubscriptionPage = useCallback(() => {
    if (currentRootId) {
      const subscriptionUpgradePath = generatePath(
        routes.homeSubscription.upgrade(),
        {
          homeId,
          rootDomainId: currentRootId,
        }
      )
      history.push(subscriptionUpgradePath)
    }
  }, [currentRootId, history, homeId])

  const openSubscriptionDetailsPage = useCallback(() => {
    if (currentRootId) {
      const subscriptionDetailsPath = generatePath(
        routes.homeSubscription.details(),
        {
          homeId,
          rootDomainId: currentRootId,
        }
      )
      history.push(subscriptionDetailsPath)
    }
  }, [currentRootId, history, homeId])

  const openSubscriptionRenewalsPage = useCallback(() => {
    if (currentRootId) {
      const subscriptionDetailsPath = generatePath(
        routes.homeSubscription.renewals(),
        {
          homeId,
          rootDomainId: currentRootId,
        }
      )
      history.push(subscriptionDetailsPath)
    }
  }, [currentRootId, history, homeId])

  const openUpdateSubscriptionPage = useCallback(() => {
    if (currentRootId) {
      const subscriptionUpdatePath = generatePath(
        routes.homeSubscription.update(),
        {
          homeId,
          rootDomainId: currentRootId,
        }
      )
      history.push(subscriptionUpdatePath)
    }
  }, [currentRootId, history, homeId])

  const handleCancelSubscription = () => {
    setSubsCancelOpen(true)
  }
  const planName = getSubscriptionPlanName(plan, formatMessage)
  const networkPlanName = formatMessage(
    subscriptionInfoMessages.networkPlanName
  )

  const subscriptionTitle = useMemo(() => {
    if (subscriptionState === SUBSCRIPTION_STATE.ENROLLED) {
      return formatMessage(subscriptionInfoMessages.enrolledPlan, { planName })
    }
    if (subscriptionState === SUBSCRIPTION_STATE.EXPIRES) {
      return formatMessage(subscriptionInfoMessages.expiringPlan, { planName })
    }
    if (subscriptionState === SUBSCRIPTION_STATE.WILL_NOT_RENEW) {
      return formatMessage(subscriptionInfoMessages.nonRenewalPlan, {
        planName: formatMessage(subscriptionInfoMessages.essentialPlanName),
      })
    }
    return formatMessage(subscriptionInfoMessages.subscriptionUpgrade)
  }, [subscriptionState, planName, formatMessage])

  const subscriptionNetworkTitle = useMemo(() => {
    if (networkSubscriptionState === SUBSCRIPTION_STATE.ENROLLED) {
      return formatMessage(subscriptionInfoMessages.enrolledPlan, {
        planName: networkPlanName,
      })
    }
    if (networkSubscriptionState === SUBSCRIPTION_STATE.EXPIRES) {
      return formatMessage(subscriptionInfoMessages.expiringPlan, {
        planName: networkPlanName,
      })
    }
    if (networkSubscriptionState === SUBSCRIPTION_STATE.WILL_NOT_RENEW) {
      return formatMessage(subscriptionInfoMessages.nonRenewalPlan, {
        planName: networkPlanName,
      })
    }
    return formatMessage(subscriptionInfoMessages.subscriptionUpgrade)
  }, [networkSubscriptionState, networkPlanName, formatMessage])

  return (
    <SubInfoBlock $isChild={props.isDetailsPage}>
      {subsCancelOpen && (
        <CancelSubscriptionModal
          onClose={() => setSubsCancelOpen(false)}
          homeId={homeId}
          periodEnd={subscriptionInfo?.expiresAt}
        />
      )}
      {isFetching || isRenewing ? (
        <SpinnerCentered />
      ) : (
        <>
          <SectionLabel>
            <span>
              <FormattedMessage
                {...subscriptionInfoMessages.subscriptionStatus}
              />
            </span>
            {props.isHomeDashboard && (
              <CommonLink to={subscriptionPath}>
                <StyledIcon path={mdiArrowRight} size={1} />
              </CommonLink>
            )}
            {props.hasShowMore && !props.isHomeDashboard && (
              <CommonLinkButton onClick={openSubscriptionDetailsPage}>
                <FormattedMessage {...subscriptionInfoMessages.moreDetails} />
              </CommonLinkButton>
            )}
          </SectionLabel>

          <SubInfoBody>
            <SubscriptionInfoBlock>
              <SubscriptionInfoPlanInfo>
                <SubscriptionInfoPlanBlock>
                  {subscriptionInfo ? (
                    <SubscriptionIcon subscriptionInfo={subscriptionInfo} />
                  ) : (
                    <StyledIcoBasic path={mdiAlertDecagram} size={1} />
                  )}
                  <SubscriptionInfoManagedByLabel>
                    {subscriptionTitle}
                  </SubscriptionInfoManagedByLabel>
                </SubscriptionInfoPlanBlock>
              </SubscriptionInfoPlanInfo>

              {networkSubscriptionInfo && (
                <SubscriptionInfoPlanInfo>
                  <SubscriptionInfoPlanBlock>
                    {networkSubscriptionInfo && (
                      <SubscriptionIcon
                        subscriptionInfo={networkSubscriptionInfo}
                      />
                    )}
                    <SubscriptionInfoManagedByLabel>
                      {subscriptionNetworkTitle} (
                      {networkSubscriptionInfo?.count})
                    </SubscriptionInfoManagedByLabel>
                  </SubscriptionInfoPlanBlock>
                </SubscriptionInfoPlanInfo>
              )}

              <SubsInfoBottomSection>
                <SubsInfoBottomSectionLeftBlock>
                  {subscriptionState !== SUBSCRIPTION_STATE.NONE && (
                    <>
                      <SubscriptionInfoStyledManageButton
                        type="button"
                        variant="secondary"
                        onClick={openSubscriptionRenewalsPage}
                        disabled={
                          !hasSubscriptionPermission
                        }
                      >
                        {formatMessage(subscriptionInfoMessages.manage)}
                      </SubscriptionInfoStyledManageButton>

                      {props.isDetailsPage && false && (
                        <SubsInfoCancelSubscription
                          as="button"
                          disabled={!hasSubscriptionPermission}
                          onClick={handleCancelSubscription}
                        >
                          <FormattedMessage
                            {...subscriptionInfoMessages.cancelSubscription}
                          />
                        </SubsInfoCancelSubscription>
                      )}
                    </>
                  )}

                  {subscriptionState === SUBSCRIPTION_STATE.NONE && false && (
                    <SubscriptionInfoStyledManageButton
                      type="button"
                      variant={
                        subscriptionState === SUBSCRIPTION_STATE.EXPIRES
                          ? 'primary'
                          : 'secondary'
                      }
                      disabled={
                        !hasSubscriptionPermission || !subscriptionEnabled
                      }
                      onClick={openUpdateSubscriptionPage}
                    >
                      {formatMessage(subscriptionInfoMessages.resubscribe)}
                    </SubscriptionInfoStyledManageButton>
                  )}

                  {subscriptionState === SUBSCRIPTION_STATE.NONE &&
                    subscriptionEnabled && (
                      <SubscriptionInfoStyledManageButton
                        type="button"
                        variant="primary"
                        disabled={
                          !hasSubscriptionPermission
                        }
                        onClick={openUpgradeSubscriptionPage}
                      >
                        {formatMessage(subscriptionInfoMessages.upgrade)}
                      </SubscriptionInfoStyledManageButton>
                    )}

                  {false && subscriptionEnabled && (
                    <SubscriptionInfoStyledManageButton
                      type="button"
                      variant="secondary"
                      disabled={
                        !hasSubscriptionPermission
                      }
                      onClick={openUpgradeSubscriptionPage}
                    >
                      {formatMessage(subscriptionInfoMessages.manage)}
                    </SubscriptionInfoStyledManageButton>
                  )}

                  {hasSubscriptionPermission && !subscriptionEnabled && (
                    <SubsInfoTooltipWrapper>
                      <Tooltip
                        text={formatMessage(
                          subscriptionInfoMessages.noValidVersion
                        )}
                        showOn="click"
                      >
                        <Icon
                          path={mdiInformationOutline}
                          size={1}
                          color={theme.colors.horizonOrange}
                        />
                      </Tooltip>
                    </SubsInfoTooltipWrapper>
                  )}

                  {!hasSubscriptionPermission && (
                    <SubsInfoTooltipWrapper>
                      <Tooltip
                        text={formatMessage(
                          subscriptionInfoMessages.noPurchasePermission
                        )}
                        showOn="click"
                      >
                        <Icon
                          path={mdiInformationOutline}
                          size={1}
                          color={theme.colors.horizonOrange}
                        />
                      </Tooltip>
                    </SubsInfoTooltipWrapper>
                  )}
                </SubsInfoBottomSectionLeftBlock>

                {subscriptionInfo && (
                  <SubscriptionInfoExpireBlock>
                    <SubscriptionInfoExpiryLabel
                      $hasWarn={
                        subscriptionState === SUBSCRIPTION_STATE.EXPIRES
                      }
                    >
                      {subscriptionState === SUBSCRIPTION_STATE.ENROLLED
                        ? formatMessage(subscriptionInfoMessages.renewalDate, {
                            date: (
                              <IntlFormattedDate
                                date={subscriptionInfo.autoRenew}
                              />
                            ),
                            br: <br />,
                          })
                        : formatMessage(
                            subscriptionInfoMessages.subscriptionExpiry,
                            {
                              endDate: (
                                <IntlFormattedDate
                                  date={subscriptionInfo.expiresAt}
                                />
                              ),
                              br: <br />,
                            }
                          )}
                    </SubscriptionInfoExpiryLabel>
                  </SubscriptionInfoExpireBlock>
                )}
              </SubsInfoBottomSection>
            </SubscriptionInfoBlock>
          </SubInfoBody>
        </>
      )}
    </SubInfoBlock>
  )
}

export default SubscriptionInfo
